import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/group-preview-card.css';

const GroupPreviewCard = ({ group, userRanking, onCopyCode }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Get rank display with medal
  const getRankDisplay = (rank) => {
    if (!rank) return { emoji: '📊', label: '—' };
    switch (rank) {
      case 1: return { emoji: '🥇', label: '1st' };
      case 2: return { emoji: '🥈', label: '2nd' };
      case 3: return { emoji: '🥉', label: '3rd' };
      default: return { emoji: null, label: `#${rank}` };
    }
  };

  // Format percentage
  const formatPercentage = (percent) => {
    if (percent === null || percent === undefined) return '+0.0%';
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  // Get movement indicator
  const getMovementIndicator = (movement) => {
    if (!movement || movement === 0) return null;
    if (movement > 0) return { icon: '▲', class: 'up', text: `Up ${movement}` };
    return { icon: '▼', class: 'down', text: `Down ${Math.abs(movement)}` };
  };

  const rankInfo = getRankDisplay(userRanking?.rank || 0);
  const movement = getMovementIndicator(userRanking?.movement);
  const memberCount = group.members?.length || group.memberCount || 0;
  const groupCode = group.uniqueNumber || group.groupCode;

  // Calculate gap to leader
  const gapToLeader = userRanking?.gapToLeader;
  const leaderName = userRanking?.leaderName;
  const leaderPerformance = userRanking?.leaderPerformance;

  const handleClick = () => {
    navigate(`/groups/${group._id || group.id}`);
  };

  const handleCopyCode = async (e) => {
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(groupCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onCopyCode) {
        onCopyCode(groupCode, e);
      }
    } catch (error) {
      // Fallback for older browsers
      alert(`Group Code: ${groupCode}\nCopy this code to share with friends!`);
    }
  };

  return (
    <div className="group-preview-card" onClick={handleClick}>
      {/* Rank Badge */}
      <div className={`preview-rank-badge rank-${userRanking?.rank || 'none'}`}>
        {rankInfo.emoji ? (
          <span className="rank-emoji">{rankInfo.emoji}</span>
        ) : (
          <span className="rank-number">{rankInfo.label}</span>
        )}
      </div>

      {/* Main Content */}
      <div className="preview-content">
        {/* Header Row */}
        <div className="preview-header">
          <h3 className="preview-group-name">{group.name}</h3>
          <div className="preview-performance">
            <span className={`performance-value ${(userRanking?.profitLossPercent || 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(userRanking?.profitLossPercent)}
            </span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="preview-stats" style={{ background: 'transparent' }}>
          <span
            className="stat-item members"
            style={{
              color: '#6B7280',
              background: '#F3F4F6',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #E5E7EB',
              fontSize: '0.85rem',
              fontWeight: '500',
              transform: 'none',
              boxShadow: 'none'
            }}
          >
            {memberCount} member{memberCount !== 1 ? 's' : ''}
          </span>
          {movement && (
            <span
              className={`stat-item movement ${movement.class}`}
              style={{
                background: 'transparent',
                padding: '0',
                border: 'none',
                transform: 'none',
                boxShadow: 'none'
              }}
            >
              {movement.icon} {movement.text} this week
            </span>
          )}
        </div>

        {/* Progress to Leader (if not #1) */}
        {userRanking?.rank > 1 && gapToLeader !== undefined && (
          <div className="preview-progress">
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${Math.min(100, 100 - (gapToLeader * 10))}%` }}
              />
            </div>
            <span className="progress-text">
              {leaderName && `Leader: ${leaderName}`}
              {leaderPerformance !== undefined && ` (${formatPercentage(leaderPerformance)})`}
              {gapToLeader !== undefined && ` • Gap: ${gapToLeader.toFixed(1)}%`}
            </span>
          </div>
        )}

        {/* First Place Indicator */}
        {userRanking?.rank === 1 && (
          <div className="preview-leader-badge">
            <span className="leader-icon">👑</span>
            <span className="leader-text">You're leading!</span>
          </div>
        )}
      </div>

      {/* Code Badge & Actions */}
      <div className="preview-actions">
        <div
          className={`code-badge ${copied ? 'copied' : ''}`}
          onClick={handleCopyCode}
          title="Copy group code"
          style={copied ? {
            background: 'linear-gradient(135deg, #059669, #10B981)',
            borderColor: '#059669'
          } : {}}
        >
          <span className="code-value" style={copied ? { color: '#FFFFFF' } : {}}>
            {copied ? 'Copied!' : groupCode}
          </span>
          <span className="copy-icon" style={copied ? { color: '#FFFFFF' } : {}}>
            {copied ? '✓' : '📋'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GroupPreviewCard;
